import React from 'react';
import {
    Modal,
    Box,
    Typography,
    Chip,
    IconButton,
    Button,
    Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Project } from '../../types';
import { cleanMarkdown, getProjectStateLabel } from '../../utils';

interface ProjectModalProps {
    open: boolean;
    project: Project | null;
    onClose: () => void;
}

const ProjectModal: React.FC<ProjectModalProps> = ({ open, project, onClose }) => {
    if (!project) return null;

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="project-modal-title"
            aria-describedby="project-modal-description"
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: '80%', md: '600px' },
                maxHeight: '90vh',
                overflow: 'auto',
                background: 'rgba(20, 20, 20, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
                p: 0,
            }}>
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                }}>
                    <Box sx={{ flex: 1, mr: 2 }}>
                        <Typography id="project-modal-title" variant="h5" component="h2" gutterBottom>
                            {project.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            <Chip
                                label={getProjectStateLabel(project.state)}
                                color="primary"
                                size="small"
                            />
                            {project.lead && (
                                <Chip
                                    label={`Lead: ${project.lead.name}`}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ p: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                            Project Description
                        </Typography>
                        {(project.description || project.content) ? (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: 2,
                                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                                        color: 'text.primary',
                                        marginTop: 1,
                                        marginBottom: 0.25,
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                    },
                                    '& h1': {
                                        fontSize: '0.95rem',
                                    },
                                    '& h2': {
                                        fontSize: '0.925rem',
                                    },
                                    '& p': {
                                        color: 'text.primary',
                                        lineHeight: 1.4,
                                        marginBottom: 0.5,
                                        fontSize: '0.8rem',
                                    },
                                    '& ul, & ol': {
                                        color: 'text.primary',
                                        paddingLeft: 1.25,
                                        fontSize: '0.8rem',
                                    },
                                    '& li': {
                                        marginBottom: 0.125,
                                    },
                                    '& code': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        padding: '1px 2px',
                                        borderRadius: 1,
                                        fontSize: '0.75em',
                                    },
                                    '& pre': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        padding: 1,
                                        borderRadius: 1,
                                        overflow: 'auto',
                                        fontSize: '0.75rem',
                                    },
                                    '& blockquote': {
                                        borderLeft: '2px solid rgba(255, 255, 255, 0.3)',
                                        paddingLeft: 1,
                                        marginLeft: 0,
                                        fontStyle: 'italic',
                                        fontSize: '0.8rem',
                                    },
                                    '& a': {
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        fontSize: '0.8rem',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    },
                                }}
                            >
                                <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                                    {cleanMarkdown(project.content || project.description || '')}
                                </ReactMarkdown>
                            </Paper>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No description available for this project.
                            </Typography>
                        )}

                        {/* Show documents if available */}
                        {project.documents?.nodes && project.documents.nodes.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                                    Project Documents
                                </Typography>
                                {project.documents.nodes.map((doc, index) => (
                                    <Paper
                                        key={index}
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            mb: 2,
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                            {doc.title}
                                        </Typography>
                                        <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                                            {cleanMarkdown(doc.content)}
                                        </ReactMarkdown>
                                    </Paper>
                                ))}
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button variant="contained" onClick={onClose}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export default ProjectModal;
